import { ref } from 'vue'
import { usePlatform } from './usePlatform'

let listenerCleanupFns = []

export const usePushNotifications = () => {
  const { isNative } = usePlatform()
  const isRegistered = ref(false)
  const deviceToken = ref(null)
  const error = ref(null)

  const requestPermissions = async () => {
    if (!isNative) {
      console.log('Push notifications only available on native platforms')
      return { granted: false }
    }

    try {
      const { PushNotifications } = await import('@capacitor/push-notifications')

      const permission = await PushNotifications.requestPermissions()

      return {
        granted: permission.receive === 'granted',
        permission: permission.receive
      }
    } catch (err) {
      console.error('Failed to request push notification permissions:', err)
      error.value = err.message
      return { granted: false, error: err }
    }
  }

  const checkPermissions = async () => {
    if (!isNative) return { granted: false }

    try {
      const { PushNotifications } = await import('@capacitor/push-notifications')
      const permission = await PushNotifications.checkPermissions()

      return {
        granted: permission.receive === 'granted',
        permission: permission.receive
      }
    } catch (err) {
      return { granted: false, error: err }
    }
  }

  const register = async () => {
    if (!isNative) {
      console.log('Push notifications only available on native platforms')
      return false
    }

    try {
      const { PushNotifications } = await import('@capacitor/push-notifications')

      const permissionStatus = await checkPermissions()
      if (!permissionStatus.granted) {
        const requestResult = await requestPermissions()
        if (!requestResult.granted) {
          throw new Error('Push notification permission denied')
        }
      }

      await PushNotifications.register()

      listenerCleanupFns.forEach(fn => fn())
      listenerCleanupFns = []

      const regListener = await PushNotifications.addListener('registration', (token) => {
        deviceToken.value = token.value
        isRegistered.value = true
        console.log('Push registration success, token:', token.value)
      })
      listenerCleanupFns.push(() => regListener.remove())

      const regErrorListener = await PushNotifications.addListener('registrationError', (err) => {
        error.value = err.error
        console.error('Push registration error:', err)
      })
      listenerCleanupFns.push(() => regErrorListener.remove())

      const receivedListener = await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('Push notification received:', notification)
      })
      listenerCleanupFns.push(() => receivedListener.remove())

      const actionListener = await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
        console.log('Push notification action performed:', notification)
        handleNotificationAction(notification)
      })
      listenerCleanupFns.push(() => actionListener.remove())

      return true
    } catch (err) {
      console.error('Failed to register for push notifications:', err)
      error.value = err.message
      return false
    }
  }

  const handleNotificationAction = (notification) => {
    const data = notification?.notification?.data
    if (!data) return

    if (data.orderId) {
      useRouter().push(`/my/orders/${data.orderId}`)
    } else if (data.route) {
      useRouter().push(data.route)
    }
  }

  const cleanup = () => {
    listenerCleanupFns.forEach(fn => fn())
    listenerCleanupFns = []
  }

  const getDeliveredNotifications = async () => {
    if (!isNative) return []

    try {
      const { PushNotifications } = await import('@capacitor/push-notifications')
      const notificationList = await PushNotifications.getDeliveredNotifications()
      return notificationList.notifications
    } catch (err) {
      console.error('Failed to get delivered notifications:', err)
      return []
    }
  }

  const removeAllDeliveredNotifications = async () => {
    if (!isNative) return

    try {
      const { PushNotifications } = await import('@capacitor/push-notifications')
      await PushNotifications.removeAllDeliveredNotifications()
    } catch (err) {
      console.error('Failed to remove delivered notifications:', err)
    }
  }

  const sendTokenToBackend = async (token) => {
    try {
      const { platform } = usePlatform()

      const { data, error: apiError } = await useApi('/api/me/device-token', {
        token,
        platform,
        deviceModel: null,
        osVersion: null,
        appVersion: null
      }, {
        method: 'POST'
      }, 1)

      if (apiError.value) {
        throw new Error(apiError.value.message || 'Failed to save device token')
      }

      console.log('Device token saved to backend')
      return true
    } catch (err) {
      console.error('Failed to send token to backend:', err)
      return false
    }
  }

  return {
    isRegistered,
    deviceToken,
    error,
    requestPermissions,
    checkPermissions,
    register,
    getDeliveredNotifications,
    removeAllDeliveredNotifications,
    sendTokenToBackend,
    cleanup,
    isNative
  }
}
